import React, { useEffect, useState } from "react";
import { Download, Menu, PlusSquare, Share, X, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import {
  hasDeferredPrompt,
  isIOSSafari,
  isStandalone,
  onInstallStateChange,
  platform,
  triggerNativeInstall,
} from "@/lib/install";

const DISMISS_KEY = "em_admin_install_dismissed";
const LATER_KEY = "em_admin_install_later_at";
const DAY = 24 * 60 * 60 * 1000;

const AdminInstallPromptModal = () => {
  const [open, setOpen] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [tick, setTick] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => onInstallStateChange(() => setTick((n) => n + 1)), []);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed === "1") return;
    const laterAt = Number(localStorage.getItem(LATER_KEY) || 0);
    if (Date.now() - laterAt < 7 * DAY) return;
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, []);

  const plat = platform();

  const onInstall = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (plat === "ios" || !hasDeferredPrompt()) {
        // On Android when the guest PWA is already installed, Chrome doesn't
        // fire beforeinstallprompt again for the scoped admin manifest —
        // show a platform-specific menu tutorial instead.
        setShowFallback(true);
        return;
      }
      const res = await triggerNativeInstall();
      if (res.ok) {
        toast.success("Admin app installed");
        setOpen(false);
      } else if (res.outcome === "dismissed") {
        toast("You can install any time from the browser menu");
        setOpen(false);
      } else {
        setShowFallback(true);
      }
    } finally {
      setBusy(false);
    }
  };

  const onLater = () => {
    localStorage.setItem(LATER_KEY, String(Date.now()));
    setOpen(false);
  };

  const onDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[290] flex items-end sm:items-center justify-center bg-foreground/45 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      data-testid="admin-install-modal"
    >
      <div className="relative w-full max-w-md bg-background border border-border rounded-2xl shadow-2xl overflow-hidden">
        <button
          type="button"
          onClick={onLater}
          className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-md text-foreground/60 hover:text-foreground hover:bg-foreground/5 z-10"
          aria-label="Close"
          data-testid="admin-install-close"
        >
          <X className="w-4 h-4" />
        </button>

        {!showFallback ? (
          <div className="p-6 sm:p-7">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-foreground text-background flex items-center justify-center shrink-0 shadow-md relative">
                <span className="font-serif-display text-2xl leading-none">A</span>
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-accent border-2 border-background" />
              </div>
              <div className="min-w-0">
                <p className="editorial-kicker text-foreground/50">Admin app</p>
                <p className="font-serif-display text-[13px] text-foreground/70">Own home-screen icon</p>
              </div>
            </div>

            <h2 className="font-serif-display text-2xl sm:text-[28px] leading-[1.15]">
              Install the admin app
            </h2>
            <p className="text-sm sm:text-[15px] leading-relaxed text-foreground/75 mt-3">
              Put the Eli &amp; Mag admin on your home screen as its own dark <strong>A</strong> icon.
              One tap to check RSVPs, dispatch Andreas pushes, or open the live map without hunting through browser tabs.
            </p>

            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                onClick={onDismiss}
                className="h-11 px-4 rounded-lg text-foreground/50 hover:text-foreground/80 transition-colors text-[12px] tracking-[0.14em] uppercase"
                data-testid="admin-install-dismiss"
              >
                Not for me
              </button>
              <button
                type="button"
                onClick={onLater}
                className="h-11 px-4 rounded-lg border border-border text-foreground/70 hover:text-foreground hover:border-foreground/40 transition-colors text-[12px] tracking-[0.14em] uppercase"
                data-testid="admin-install-later"
              >
                Maybe later
              </button>
              <button
                type="button"
                onClick={onInstall}
                disabled={busy}
                className="h-11 px-5 rounded-lg bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60 transition-colors text-[12px] tracking-[0.14em] uppercase inline-flex items-center justify-center gap-2"
                data-testid="admin-install-accept"
              >
                <Download className="w-4 h-4" />
                Install admin app
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 sm:p-7" data-testid={plat === "ios" ? "admin-install-ios" : "admin-install-menu"}>
            <p className="editorial-kicker text-foreground/50">Admin app</p>
            <h2 className="font-serif-display text-2xl sm:text-[26px] leading-tight mt-1">
              {plat === "ios" ? "Tap Share \u2192 Add to Home Screen" : "Open the browser menu"}
            </h2>
            <p className="text-sm text-foreground/75 mt-3">
              {plat === "ios"
                ? "Tap the Share icon at the bottom of Safari and choose Add to Home Screen. Your admin app lives beside your other apps."
                : "Your browser doesn\u2019t offer a one-tap install here because the guest app is already installed. Open the browser menu and pick \u201cInstall app\u201d or \u201cAdd to Home screen\u201d \u2014 you\u2019ll get a distinct dark A icon."}
            </p>

            <div className="mt-5 flex items-center gap-3 rounded-lg border border-border p-3">
              <div className="w-10 h-10 rounded-md bg-foreground/5 flex items-center justify-center shrink-0">
                {plat === "ios" ? <Share className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium">1 · {plat === "ios" ? "Tap the Share icon" : "Tap the menu icon"}</p>
                <p className="text-[12px] text-foreground/60">
                  {plat === "ios" ? "at the bottom of Safari" : "three dots at the top of Chrome"}
                </p>
              </div>
            </div>
            <div className="flex justify-center py-1">
              <ArrowDown className="w-4 h-4 text-foreground/30 animate-bounce" aria-hidden="true" />
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-border p-3">
              <div className="w-10 h-10 rounded-md bg-foreground/5 flex items-center justify-center shrink-0">
                {plat === "ios" ? <PlusSquare className="w-5 h-5" /> : <Download className="w-5 h-5" />}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium">2 · {plat === "ios" ? "Choose \u201cAdd to Home Screen\u201d" : "Tap \u201cInstall app\u201d"}</p>
                <p className="text-[12px] text-foreground/60">
                  {plat === "ios" ? "then tap Add \u2014 done" : "or \u201cAdd to Home screen\u201d"}
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={onLater}
                className="h-11 px-5 rounded-lg bg-foreground text-background hover:bg-foreground/90 text-[12px] tracking-[0.14em] uppercase"
                data-testid="admin-install-fallback-gotit"
              >
                Got it
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInstallPromptModal;
